<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { tokenizeMediumText, type Token, type TermToken } from "~/utils/glossaryMatcher";
import { GLOSSARY_DATA, type GlossaryEntry } from "~/data/glossaryData";
import GlossaryCategoryAnimation from "~/components/molecules/GlossaryCategoryAnimation.vue";

const props = defineProps<{
  text?: string | null;
  class?: string;
}>();

const containerRef = ref<HTMLElement | null>(null);

const tokens = computed<Token[]>(() => {
  if (!props.text) return [];
  return tokenizeMediumText(props.text);
});

const activeTokenIndex = ref<number | null>(null);

const activeEntry = computed<GlossaryEntry | null>(() => {
  if (activeTokenIndex.value === null) return null;
  const token = tokens.value[activeTokenIndex.value];
  if (token && token.type === "term") {
    return GLOSSARY_DATA[token.term] || token.entry;
  }
  return null;
});

function handleTermClick(event: MouseEvent, index: number) {
  event.stopPropagation();
  if (activeTokenIndex.value === index) {
    // Toggle off if clicking the same term
    activeTokenIndex.value = null;
  } else {
    activeTokenIndex.value = index;
  }
}

function handleDocumentClick(event: MouseEvent) {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    activeTokenIndex.value = null;
  }
}

onMounted(() => {
  if (typeof document !== "undefined") {
    document.addEventListener("click", handleDocumentClick);
  }
});

onUnmounted(() => {
  if (typeof document !== "undefined") {
    document.removeEventListener("click", handleDocumentClick);
  }
});
</script>

<template>
  <div
    v-if="text"
    ref="containerRef"
    :class="['medium-formatted-container relative block', props.class]"
  >
    <!-- 1. Text line with inline recognized technique terms -->
    <p class="inline-block text-base sm:text-lg text-on-primary-body leading-relaxed">
      <template v-if="tokens.length > 0">
        <template v-for="(tok, idx) in tokens" :key="idx">
          <!-- Recognized term with dotted underline & unified #4682B4 accent -->
          <span
            v-if="tok.type === 'term'"
            :class="[
              'medium-term-span inline transition-all duration-200 cursor-pointer select-none',
              activeTokenIndex === idx ? 'medium-term-active' : 'medium-term-idle',
            ]"
            :title="`Learn about ${tok.entry.term}`"
            @click="(e) => handleTermClick(e, idx)"
          >
            {{ tok.originalText }}
          </span>

          <!-- Regular text fragment -->
          <span v-else class="inline">{{ tok.content }}</span>
        </template>
      </template>
      <template v-else>
        {{ text }}
      </template>
    </p>

    <!-- 2. Inline Expandable Glossary Card (Matching Spec) -->
    <Transition name="expand-card">
      <div
        v-if="activeEntry"
        class="glossary-inline-card mt-3.5 mb-4 w-full rounded-2xl bg-[#f4f4f2] dark:bg-neutral-800/90 p-5 sm:p-6 text-left border border-neutral-200/60 dark:border-neutral-700/60 shadow-sm"
        @click.stop
      >
        <!-- Term Title -->
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-base sm:text-[17px] font-bold text-neutral-900 dark:text-neutral-100 tracking-tight m-0">
            {{ activeEntry.term }}
          </h4>
          <button
            type="button"
            class="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 p-1 -mr-1 transition-colors rounded-lg"
            aria-label="Close definition"
            @click="activeTokenIndex = null"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <!-- Sub-Family Category Visual (Animation or Static Texture) -->
        <GlossaryCategoryAnimation
          :category="activeEntry.category"
          :sub-family="activeEntry.subFamily"
          :term="activeEntry.term"
          :image-path="activeEntry.imagePath"
        />

        <!-- Plain-Language Definition -->
        <p class="text-[13.5px] sm:text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed font-normal mt-3.5 mb-0">
          {{ activeEntry.definition }}
        </p>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.medium-term-idle {
  color: inherit;
  border-bottom: 1.5px dotted #999999;
}

.medium-term-idle:hover {
  color: #4682B4;
  border-bottom: 1.5px solid #4682B4;
}

.medium-term-active {
  color: #4682B4;
  border-bottom: 1.5px solid #4682B4;
  font-weight: 500;
}

/* Smooth expansion animation */
.expand-card-enter-active,
.expand-card-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.expand-card-enter-from,
.expand-card-leave-to {
  opacity: 0;
  transform: translateY(-6px);
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.expand-card-enter-to,
.expand-card-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 550px;
}
</style>
